<!-- Hero Section -->
<section class="relative py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 overflow-hidden">
  <div class="absolute inset-0 opacity-20">
    <img src="https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?w=1920&h=800&fit=crop" alt="" class="w-full h-full object-cover">
  </div>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
    <nav class="text-sm text-white/70 mb-6">
      <a routerLink="/" class="hover:text-white">Home</a>
      <span class="mx-2">/</span>
      <a routerLink="/devops" class="hover:text-white">DevOps & Automation</a>
      <span class="mx-2">/</span>
      <span class="text-white">CI/CD Pipeline Automation</span>
    </nav>
    <div class="text-center text-white">
      <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">CI/CD Pipeline Automation</h1>
      <p class="text-xl text-white/90 max-w-3xl mx-auto mb-8">
        Ship faster and safer with automated build, test, and deployment pipelines. Reduce deployment time from hours to minutes
        while increasing reliability and developer productivity.
      </p>
      <a routerLink="/contact" class="inline-block bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-primary-50 transition-colors">
        Get Your Free Pipeline Assessment
      </a>
    </div>
  </div>
</section>

<!-- Features Section -->
<section class="py-20 bg-white">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="text-center mb-16">
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What We Offer</h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        End-to-end CI/CD solutions tailored to your development workflow.
      </p>
    </div>
    <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
      @for (feature of features; track feature.title) {
        <div class="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300">
          <div class="w-14 h-14 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl flex items-center justify-center mb-6">
            <svg class="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-3">{{ feature.title }}</h3>
          <p class="text-gray-600">{{ feature.description }}</p>
        </div>
      }
    </div>
  </div>
</section>

<!-- Process Section -->
<section class="py-20 bg-gray-50">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="text-center mb-16">
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Process</h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        A proven approach to implementing CI/CD pipelines that deliver results.
      </p>
    </div>
    <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
      @for (item of process; track item.step) {
        <div class="text-center">
          <div class="w-16 h-16 bg-gradient-to-br from-primary-600 to-primary-700 rounded-full flex items-center justify-center mx-auto mb-6">
            <span class="text-2xl font-bold text-white">{{ item.step }}</span>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-3">{{ item.title }}</h3>
          <p class="text-gray-600">{{ item.description }}</p>
        </div>
      }
    </div>
  </div>
</section>

<!-- Case Study Section -->
<section class="py-20 bg-white">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="text-center mb-16">
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Case Study</h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        See how we helped {{ caseStudy.client }} transform their deployment process.
      </p>
    </div>
    <div class="bg-gradient-to-br from-primary-50 to-primary-100 rounded-3xl overflow-hidden">
      <div class="grid lg:grid-cols-2 gap-0">
        <div class="p-8 lg:p-12">
          <div class="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">
            {{ caseStudy.industry }}
          </div>
          <h3 class="text-2xl md:text-3xl font-bold text-gray-900 mb-6">{{ caseStudy.client }}</h3>

          <div class="space-y-6">
            <div>
              <h4 class="font-semibold text-gray-900 mb-2">The Challenge</h4>
              <p class="text-gray-600">{{ caseStudy.challenge }}</p>
            </div>
            <div>
              <h4 class="font-semibold text-gray-900 mb-2">Our Solution</h4>
              <p class="text-gray-600">{{ caseStudy.solution }}</p>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4 mt-8">
            @for (result of caseStudy.results; track result.description) {
              <div class="bg-white rounded-xl p-4 shadow-sm">
                <div class="text-2xl font-bold text-primary-600">{{ result.metric }}</div>
                <div class="text-sm text-gray-600">{{ result.description }}</div>
              </div>
            }
          </div>
        </div>
        <div class="h-64 lg:h-auto">
          <img [src]="caseStudy.image" alt="Case Study" class="w-full h-full object-cover">
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Testimonial Section -->
<section class="py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
  <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
    <svg class="w-12 h-12 text-yellow-400 mx-auto mb-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
    </svg>
    <p class="text-xl md:text-2xl text-white mb-8 italic">
      "{{ testimonial.quote }}"
    </p>
    <div class="flex items-center justify-center">
      <img [src]="testimonial.image" [alt]="testimonial.author" class="w-14 h-14 rounded-full object-cover mr-4">
      <div class="text-left">
        <div class="font-semibold text-white">{{ testimonial.author }}</div>
        <div class="text-white/70">{{ testimonial.role }}, {{ testimonial.company }}</div>
      </div>
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="py-20 bg-white">
  <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
    <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Ready to Automate Your Deployments?</h2>
    <p class="text-xl text-gray-600 mb-8">
      Get a free pipeline assessment and discover how we can accelerate your software delivery.
    </p>
    <div class="flex flex-col sm:flex-row gap-4 justify-center">
      <a routerLink="/contact" class="btn-primary">Schedule Your Assessment</a>
      <a routerLink="/devops" class="btn-secondary">Explore All DevOps Services</a>
    </div>
  </div>
</section>
